export type HouseholdReviewDecision = "use_existing" | "create_new" | "exclude" | null

export interface HouseholdReviewPerson {
  decision: HouseholdReviewDecision
  selectedPersonId: string | null
}

export interface HouseholdReviewSelection {
  selectedPersonId: string | null
  selectedPersonSlot?: string | null
  accountHolderRelationship?: number | null
  relationships?: Record<string, number>
  /** A prior alternate applicant or relationship map needs explicit staff reconciliation. */
  invalidated?: boolean
  relationshipReviewRequired?: boolean
}

export interface HouseholdReviewDocument {
  accountHolder: HouseholdReviewPerson
  accountHolderHouseholdExclusionReason?: string | null
  coCustomer: HouseholdReviewPerson | null
  householdMembers: Array<HouseholdReviewPerson & { index: number }>
  applicantSelection?: HouseholdReviewSelection | null
}

export function householdMemberSlot(index: number): string {
  return `householdMembers[${index}]`
}

/** Returns stable slots that are not explicitly excluded, including pending rows. */
export function includedApplicantSlots(document: HouseholdReviewDocument): string[] {
  const slots = document.accountHolderHouseholdExclusionReason ? [] : ["accountHolder"]
  if (document.coCustomer) slots.push("coCustomer")
  for (const member of document.householdMembers) {
    if (member.decision !== "exclude") slots.push(householdMemberSlot(member.index))
  }
  return slots
}

/** Alternate applicants must be reviewed person decisions, never pending or excluded rows. */
export function reviewedApplicantSlots(document: HouseholdReviewDocument): string[] {
  const slots: string[] = []
  if (document.coCustomer?.decision === "use_existing" || document.coCustomer?.decision === "create_new") {
    slots.push("coCustomer")
  }
  for (const member of document.householdMembers) {
    if (member.decision === "use_existing" || member.decision === "create_new") {
      slots.push(householdMemberSlot(member.index))
    }
  }
  return slots
}

/**
 * Retains an unaffected applicant while making membership changes explicit. A changed selected
 * row is marked invalidated instead of becoming the valid account-holder default. When a different
 * row changes membership, stale relationship entries are removed and a visible confirmation is
 * required before creation can continue.
 */
export function reconcileApplicantSelection(
  document: HouseholdReviewDocument,
  changedSlot: string,
  membershipChanged: boolean,
): HouseholdReviewSelection | null {
  const current = document.applicantSelection
  if (!current) return null

  const includedSlots = includedApplicantSlots(document)
  const selectedSlot = current.selectedPersonSlot ??
    (current.selectedPersonId
      ? includedSlots.find((slot) => personForSlot(document, slot)?.selectedPersonId === current.selectedPersonId)
      : undefined)
  const selectedWasChanged = selectedSlot === changedSlot ||
    (!current.selectedPersonSlot && current.selectedPersonId === personForSlot(document, changedSlot)?.selectedPersonId)
  const selectedIsIncluded = selectedSlot ? includedSlots.includes(selectedSlot) : false

  if (selectedWasChanged || !selectedIsIncluded) {
    return {
      ...current,
      invalidated: true,
      relationshipReviewRequired: true,
      relationships: filterRelationships(current.relationships, includedSlots),
    }
  }

  const relationships = filterRelationships(current.relationships, includedSlots)
  if (!membershipChanged) {
    // A different canonical person in the same slot must not inherit the previous person's
    // relationship to the applicant, even though household membership/count did not change.
    delete relationships[changedSlot]
  }
  return {
    ...current,
    relationships,
    relationshipReviewRequired: true,
  }
}

/** Explicit staff action only. Preserve known choices; never include excluded or stale slots. */
export function fillUnansweredRelationships(
  document: HouseholdReviewDocument,
  applicantSlot: string,
): Record<string, number> | null {
  const slots = includedApplicantSlots(document)
  if (document.applicantSelection?.invalidated || !slots.includes(applicantSlot)) return null
  if (slots.some(slot => {
    const person = personForSlot(document, slot)
    return !person || !["use_existing", "create_new"].includes(person.decision ?? "") ||
      (person.decision === "use_existing" && !person.selectedPersonId)
  })) return null
  const relationships = filterRelationships(document.applicantSelection?.relationships, slots)
  for (const slot of slots) {
    if (slot === applicantSlot) relationships[slot] = 0
    else if (relationships[slot] == null || relationships[slot] === 255) relationships[slot] = 7
  }
  return relationships
}

function personForSlot(document: HouseholdReviewDocument, slot: string): HouseholdReviewPerson | null {
  if (slot === "accountHolder") return document.accountHolder
  if (slot === "coCustomer") return document.coCustomer
  const match = /^householdMembers\[(\d+)]$/.exec(slot)
  if (!match) return null
  return document.householdMembers.find((member) => member.index === Number(match[1])) ?? null
}

function filterRelationships(
  relationships: Record<string, number> | undefined,
  includedSlots: string[],
): Record<string, number> {
  if (!relationships) return {}
  const allowed = new Set(includedSlots)
  return Object.fromEntries(Object.entries(relationships).filter(([slot]) => allowed.has(slot)))
}
